import { useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { AlertTriangle, Info, Search } from 'lucide-react';
import type { KpiDefinition, KpiPersonYear, KpiTargetRow, ScopeType } from '../../../types/strategicKpis';
import { CATEGORY_LABEL, kpiLabel, formatKpiValue } from '../../../utils/strategicKpiFormat';
import { SKPI_FAMILIES, TARGET_LEVELS, type SkpiFamily, type TargetLevel } from '../../../utils/strategicKpiUrl';
import {
    LEVEL_LABEL, LEVEL_ROW_LABEL, derivedTargetState, familyLock, familyLockText, familyTargetCount, gridColumns,
    priorYearHasTargets, showsDeviationDot, targetGridRows, targetGridView, targetKey, type TargetGridRow,
} from '../../../utils/strategicKpiTargetGrid';
import { TargetCell } from '../TargetCell';
import { useWhatsNewAnchor, NewMarker } from '../../../whats-new';
import { useCardTableOffset } from '../useTableOffset';

/** K2 (D6): the total visit target — stored and edited here; C is calculated from it by the server. */
const TOTAL_VISITS_KPI = 'visits.target_visits_ytd';
/** What's New entry 2026-10-07-skpi-total-visits-target marks the Total column (§2 rule 12). */
const TOTAL_ANCHOR = 'column:skpi-total-visits-target';

const NAME_WIDTH = 240;
const KPI_WIDTH = 128;
/** The gap below the card to the viewport edge (the scroller's p-6), as in SteeredKpiRows. */

const SMALL: CSSProperties = { fontSize: 'var(--fs-small)', lineHeight: 'var(--lh-small)', color: 'var(--text-muted)' };
/** The first column stays in view when the table scrolls sideways (as DrillDownTable). */
const sticky = (head: boolean): CSSProperties => ({ position: 'sticky', left: 0, zIndex: head ? 3 : 1, background: head ? 'var(--thead-bg)' : 'var(--surface)', borderRight: '1px solid var(--hair)' });
const FOOT_NAME: CSSProperties = Object.assign(sticky(false), { background: 'var(--tfoot-bg)', zIndex: 3 });
const FOOT_COUNT: CSSProperties = Object.assign({}, SMALL, { marginLeft: 4, fontWeight: 450 });
/** The table box sits flush in the card: no side, bottom or rounded edge of its own. */
const WRAP_EDGES: CSSProperties = { borderLeft: 0, borderRight: 0, borderBottom: 0, borderRadius: 0 };

export interface TargetGridProps {
    /** The target year the grid edits. The prior year is `year − 1`. */
    year: number;
    level: TargetLevel;
    onLevelChange: (level: TargetLevel) => void;
    family: SkpiFamily;
    onFamilyChange: (family: SkpiFamily) => void;
    /** The KPI catalogue (GET definitions); the grid takes the active KPIs of the family that fit the level. */
    defs: KpiDefinition[];
    /** The persons of the page's BUs that the selection leaves (K3) — the rows, and the members of the group rows. */
    persons: KpiPersonYear[];
    /** All persons of the page's BUs, unfiltered — for «n of m persons selected» on a group the selection cuts. */
    allPersons: KpiPersonYear[];
    /** The page's BUs (the rows of the BU level, in this order). */
    bus: string[];
    /** StaffID → name of the year's persons (team rows are named after their manager). */
    nameOf: Map<string, string>;
    /** The year's targets, keyed `targetKey(kpiKey, scopeType, scopeKey)`; derived rows (C) as GET targets returns them. */
    targets: Map<string, KpiTargetRow>;
    /** The prior year's targets, same key; null = not loaded (no prior line, no dots). */
    prior: Map<string, KpiTargetRow> | null;
    /** The prior year's targets failed to load: the card head says so (no dots, no prior line follow). */
    priorError?: boolean;
    canEdit: boolean;
    /** Keys (`targetKey`) whose PUT is running. */
    saving: Set<string>;
    /** The PUT path of the tab (unchanged, E19): resolves true when stored. */
    onSave: (kpi: KpiDefinition, scopeType: ScopeType, scopeKey: string, value: number | null) => Promise<boolean>;
    /** The engine's Σ of the person targets of a group node (second line of a group cell), or null. */
    personsSum?: (scopeType: ScopeType, scopeKey: string, kpiKey: string) => number | null;
    /** The card's title; default «Targets {year}». */
    heading?: string;
    /** Beside the title (card-head end), e.g. the change-log button. */
    headEnd?: ReactNode;
    /**
     * The year's data is (re)loading: the table is busy and no cell opens — it would show the values of the year
     * still on screen, and TargetCell skips the PUT for an unchanged value. The status line shows the spinner.
     */
    loading?: boolean;
}

type Sort = { key: string | null; direction: 'asc' | 'desc' };

/**
 * The target correction grid (masterplan skpi-redesign C3a; E5, E11): one level — persons, teams, companies or the
 * business unit — and exactly one KPI family, as one card whose table is the last block of the tab (§2 rule 5).
 *
 * Which KPIs a level shows is the logic of the former PersonGrid / GroupGrid, cut by level (§2 rule 7): a KPI with
 * the level's bit in `TargetGrain`, plus the derived C-visit target beside them. A family with targets but none at
 * this level (BAB on persons and teams) is locked: its segment is marked, and selecting it shows a notice instead of
 * the table. Every stored cell is the unchanged `TargetCell` (§2 rule 13); the grid renders beside it the E11 dot (the
 * target differs from the prior year's) and, under it, the prior year's target. C visits is the server's value,
 * read-only, with a warning when it is negative (Total below A + B).
 */
export function TargetGrid({
    year, level, onLevelChange, family, onFamilyChange, defs, persons, allPersons, bus, nameOf, targets, prior, priorError = false,
    canEdit, saving, onSave, personsSum, heading, headEnd, loading = false,
}: TargetGridProps) {
    const card = useRef<HTMLElement>(null);
    const wrap = useRef<HTMLDivElement>(null);
    const [search, setSearch] = useState('');
    const [sort, setSort] = useState<Sort>({ key: null, direction: 'asc' });
    // No `reveal`: the column lives in this tab, which the reader has already opened to see the marker.
    const totalAnchor = useWhatsNewAnchor(TOTAL_ANCHOR);

    // A search for a person means nothing on the company level: a level switch starts with an empty search.
    const [searchLevel, setSearchLevel] = useState(level);
    if (searchLevel !== level) { setSearchLevel(level); setSearch(''); }

    const columns = useMemo(() => gridColumns(defs, family, level), [defs, family, level]);
    /** The grain lock of every family at this level, computed once (null = not locked here). */
    const locks = useMemo(() => new Map(SKPI_FAMILIES.map(f => [f, familyLock(defs, f, level, TARGET_LEVELS)] as const)), [defs, level]);
    const lock = locks.get(family) ?? null;
    const familyLabel = CATEGORY_LABEL[family] ?? family;
    const lockedHere = useMemo(() => SKPI_FAMILIES.filter(f => locks.get(f) != null), [locks]);
    const priorYear = year - 1;
    const markDiffs = priorYearHasTargets(prior);
    const editable = canEdit && !loading;

    const valueOf = (row: Pick<TargetGridRow, 'scopeType' | 'scopeKey'>, kpiKey: string, from: Map<string, KpiTargetRow> | null = targets) =>
        from?.get(targetKey(kpiKey, row.scopeType, row.scopeKey))?.TargetValue ?? null;

    const allRows = useMemo(() => targetGridRows(level, persons, allPersons, bus, nameOf), [level, persons, allPersons, bus, nameOf]);
    // Σ persons and n/m run over all rows of the selection; the search only narrows what is shown (§2 rule 7).
    const { rows, sortKey, direction, sums, total } = useMemo(
        () => targetGridView(level, allRows, columns, search, sort, (r, k) => valueOf(r, k)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [level, allRows, columns, search, sort, targets],
    );

    const showTable = lock === null && columns.length > 0;
    const offset = useCardTableOffset(card, wrap, [showTable, level, family, columns.length, rows.length]);

    const save = (kpi: KpiDefinition, scopeType: ScopeType, scopeKey: string, value: number | null) => {
        if (kpi.KpiKey === TOTAL_VISITS_KPI) totalAnchor.markUsed();
        return onSave(kpi, scopeType, scopeKey, value);
    };

    // Against the sort in force, so a sort by a column that is gone toggles from «name ascending».
    const onSort = (key: string | null) => setSort(sortKey === key
        ? { key, direction: direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: key === null ? 'asc' : 'desc' });
    const ariaSort = (key: string | null) => (sortKey === key ? (direction === 'asc' ? 'ascending' : 'descending') : undefined);
    const caret = (key: string | null) => sortKey === key && <span className="caret" aria-hidden="true">{direction === 'asc' ? '▲' : '▼'}</span>;

    const tableWidth = NAME_WIDTH + columns.length * KPI_WIDTH;
    const wrapStyle: CSSProperties = offset !== null ? Object.assign({ '--tbl-offset': `${offset}px` } as CSSProperties, WRAP_EDGES) : WRAP_EDGES;

    const levelSeg = (
        <div className="seg" role="group" aria-label="Level" data-testid="skpi-target-level">
            {TARGET_LEVELS.map(l => (
                <button key={l} type="button" onClick={() => onLevelChange(l)} aria-pressed={level === l}>{LEVEL_LABEL[l]}</button>
            ))}
        </div>
    );
    const familySeg = (
        <div className="seg" role="group" aria-label="KPI family" data-testid="skpi-target-family">
            {SKPI_FAMILIES.map(f => {
                const locked = lockedHere.includes(f);
                const label = CATEGORY_LABEL[f] ?? f;
                return (
                    <button
                        key={f}
                        type="button"
                        onClick={() => { if (!locked) onFamilyChange(f); }}
                        aria-pressed={family === f}
                        aria-disabled={locked || undefined}
                        aria-describedby={locked ? 'skpi-target-lock-note' : undefined}
                        title={locked ? familyLockText(label, locks.get(f) ?? []) : undefined}
                        data-family={f}
                    >
                        {label} <span className="cnt">{familyTargetCount(defs, f)}</span>
                    </button>
                );
            })}
        </div>
    );
    const searchBox = (
        <div className="search">
            <Search className="i sm" aria-hidden="true" />
            <input
                type="search"
                className="input"
                placeholder={level === 'person' ? 'Find a person' : 'Find a row'}
                aria-label={level === 'person' ? 'Find a person' : `Find a ${LEVEL_ROW_LABEL[level].toLowerCase()}`}
                value={search}
                onChange={e => setSearch(e.target.value)}
                data-testid="skpi-target-search"
            />
        </div>
    );

    return (
        <section
            ref={card}
            className="card flush"
            aria-labelledby="skpi-target-grid-h"
            aria-busy={loading || undefined}
            data-testid="skpi-target-grid-card"
        >
            <div className="card-head">
                <h2 id="skpi-target-grid-h">{heading ?? `Targets ${year}`}</h2>
                <p className="sub">
                    {canEdit ? 'Click a cell, type, Enter stores · Escape cancels · empty removes · every change is logged' : 'Read-only — you have no edit right on this page'}
                    {markDiffs && <> · a dot marks a target that differs from {priorYear}</>}
                </p>
                {headEnd && <div className="end">{headEnd}</div>}
                {priorError && (
                    <p className="attn col-start-1 row-start-3 mt-1 inline-flex items-center gap-1" role="status" data-testid="skpi-target-prior-error">
                        <AlertTriangle className="i sm" aria-hidden="true" />
                        Targets {priorYear} could not be loaded
                    </p>
                )}
            </div>
            <div className="toolbar" style={{ padding: '4px 24px 16px' }}>
                {levelSeg}
                {familySeg}
                {lockedHere.length > 0 && (
                    <span id="skpi-target-lock-note" className="count" data-testid="skpi-target-lock-note">
                        {lockedHere.map(f => familyLockText(CATEGORY_LABEL[f] ?? f, locks.get(f) ?? [])).join(' ')}
                    </span>
                )}
                <span className="tb-grow" />
                {showTable && searchBox}
            </div>

            {lock !== null && (
                <div style={{ padding: '0 24px 24px' }}>
                    <div className="banner info" role="note" data-testid="skpi-target-locked">
                        <span className="ic"><Info className="i" aria-hidden="true" /></span>
                        <p>{familyLockText(familyLabel, lock)} Switch the level to {lock.map(l => LEVEL_LABEL[l]).join(' or ')}.</p>
                        <div className="actions">
                            {lock.map(l => (
                                <button key={l} type="button" className="btn secondary sm" onClick={() => onLevelChange(l)}>{LEVEL_LABEL[l]}</button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {lock === null && columns.length === 0 && (
                <div style={{ padding: '0 24px 24px' }}>
                    <div className="empty" role="status">
                        <b>No targets in {familyLabel}</b>
                        <p>No KPI of this family takes a target. Pick another family.</p>
                    </div>
                </div>
            )}

            {showTable && (
                <div
                    ref={wrap}
                    className="tbl-wrap"
                    style={wrapStyle}
                >
                    <table
                        className="tbl tbl-fixed"
                        style={{ width: tableWidth, minWidth: '100%' }}
                        data-testid="skpi-target-grid"
                        data-level={level}
                        data-family={family}
                    >
                        <colgroup>
                            <col style={{ width: NAME_WIDTH }} />
                            {columns.map(k => <col key={k.KpiKey} style={{ width: KPI_WIDTH }} />)}
                        </colgroup>
                        <thead>
                            <tr>
                                <th scope="col" aria-sort={ariaSort(null)} style={sticky(true)}>
                                    <button type="button" className="sort" onClick={() => onSort(null)}>{LEVEL_ROW_LABEL[level]}{caret(null)}</button>
                                </th>
                                {columns.map(k => {
                                    const isTotal = k.KpiKey === TOTAL_VISITS_KPI;
                                    const name = kpiLabel(k);
                                    const sub = k.DerivedTarget ? `= ${k.DerivedTarget}` : isTotal ? 'Total A + B + C' : null;
                                    return (
                                        <th key={k.KpiKey} scope="col" className="n" aria-sort={k.DerivedTarget ? undefined : ariaSort(k.KpiKey)} title={k.DerivedTarget ? `Calculated by the server as ${k.DerivedTarget} — set the Total visits target to change it` : name} data-kpi={k.KpiKey}>
                                            <span className="kh">
                                                {k.DerivedTarget
                                                    ? <span>{name}</span>
                                                    : <button type="button" className="sort" onClick={() => onSort(k.KpiKey)}>{name}{caret(k.KpiKey)}</button>}
                                                {sub && <small>{sub}{isTotal && totalAnchor.isNew && <NewMarker />}</small>}
                                            </span>
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {allRows.length === 0 && (
                                <tr><td colSpan={columns.length + 1} className="dash">{level === 'person' ? 'No persons in this year and BU.' : 'No groups in this year and BU.'}</td></tr>
                            )}
                            {allRows.length > 0 && rows.length === 0 && (
                                <tr><td colSpan={columns.length + 1} className="dash">Nothing matches «{search.trim()}».</td></tr>
                            )}
                            {rows.map(r => (
                                <tr key={`${r.scopeType}|${r.scopeKey}`} data-scope={`${r.scopeType}:${r.scopeKey}`}>
                                    <td style={sticky(false)} title={r.title ?? r.detail}>
                                        <div className="cellstack">
                                            <span className="name" style={r.inactive ? { color: 'var(--text-muted)' } : undefined}>{r.label}</span>
                                            <span className="sub2">{r.detail}{r.inactive && ' · left'}</span>
                                        </div>
                                    </td>
                                    {columns.map(k => (
                                        <td key={k.KpiKey} className="n" style={{ padding: '4px 8px' }}>
                                            <GridCell
                                                kpi={k}
                                                row={r}
                                                value={valueOf(r, k.KpiKey)}
                                                prior={prior ? valueOf(r, k.KpiKey, prior) : undefined}
                                                priorYear={priorYear}
                                                markDiffs={markDiffs}
                                                personsSum={r.scopeType !== 'PERSON' && personsSum ? personsSum(r.scopeType, r.scopeKey, k.KpiKey) : null}
                                                canEdit={editable}
                                                saving={saving.has(targetKey(k.KpiKey, r.scopeType, r.scopeKey))}
                                                onSave={v => save(k, r.scopeType, r.scopeKey, v)}
                                            />
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                        {sums && total > 0 && (
                            <tfoot>
                                <tr>
                                    <td style={FOOT_NAME}>
                                        Σ persons <span style={SMALL}>(pct / days: mean)</span>
                                    </td>
                                    {columns.map(k => {
                                        const s = sums[k.KpiKey];
                                        return (
                                            <td key={k.KpiKey} className="n" title={`${s?.n ?? 0} of ${total} persons in the selection have a target`}>
                                                {s?.value === null || s === undefined ? <span className="dash">—</span> : formatKpiValue(k.Unit, s.value)}
                                                <span style={FOOT_COUNT}>{s?.n ?? 0}/{total}</span>
                                            </td>
                                        );
                                    })}
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            )}
        </section>
    );
}

/**
 * One cell: the derived C target (read-only, warning when negative), or the unchanged TargetCell with the E11 dot
 * beside it and the prior year's target under it.
 */
function GridCell({ kpi, row, value, prior, priorYear, markDiffs, personsSum, canEdit, saving, onSave }: {
    kpi: KpiDefinition;
    row: TargetGridRow;
    value: number | null;
    /** undefined = no prior year loaded (no line). */
    prior: number | null | undefined;
    priorYear: number;
    markDiffs: boolean;
    personsSum: number | null;
    canEdit: boolean;
    saving: boolean;
    onSave: (value: number | null) => Promise<boolean>;
}) {
    const testId = row.scopeType === 'PERSON' ? `cell-${row.scopeKey}-${kpi.KpiKey}` : `gcell-${row.scopeType}-${row.scopeKey}-${kpi.KpiKey}`;
    const sumLine = personsSum !== null ? `Σ persons ${formatKpiValue(kpi.Unit, personsSum)}` : undefined;
    const priorLine = prior !== undefined && (
        <span className="block" style={SMALL} title={`Target ${priorYear}`}>{priorYear}: {formatKpiValue(kpi.Unit, prior)}</span>
    );

    if (kpi.DerivedTarget) {
        const state = derivedTargetState(value);
        return (
            <span className="flex flex-col items-end" data-testid={testId} data-derived="true" data-negative={state.kind === 'negative' || undefined}>
                {state.kind === 'negative' ? (
                    <span className="attn inline-flex items-center gap-1" title={`Total is below A + B: correct the Total or A / B (calculated as ${kpi.DerivedTarget})`}>
                        <AlertTriangle className="i sm" aria-hidden="true" />
                        {formatKpiValue(kpi.Unit, state.value)}
                        <span className="sr-only"> — negative: the Total is below A + B</span>
                    </span>
                ) : (
                    <span className="muted" title={`Calculated as ${kpi.DerivedTarget} — set the Total visits target to change it`}>
                        {state.kind === 'none' ? '—' : formatKpiValue(kpi.Unit, state.value)}
                        <span className="sr-only"> (calculated as {kpi.DerivedTarget})</span>
                    </span>
                )}
                {sumLine && <span style={SMALL}>{sumLine}</span>}
                {priorLine}
            </span>
        );
    }

    const dot = prior !== undefined && showsDeviationDot(value, prior, markDiffs);
    return (
        <div className="flex flex-col items-end" data-deviates={dot || undefined}>
            <div className="flex w-full items-center justify-end gap-1">
                {dot && (
                    <span className="dot" style={{ background: 'var(--action)' }} role="img" aria-label={`Differs from ${priorYear}`} title={`Differs from the ${priorYear} target`} data-testid={`${testId}-dot`} />
                )}
                <div className="min-w-0 flex-1">
                    <TargetCell
                        unit={kpi.Unit}
                        value={value}
                        canEdit={canEdit}
                        saving={saving}
                        onSave={onSave}
                        secondary={sumLine}
                        testId={testId}
                        label={`${kpiLabel(kpi)} — ${row.scopeType === 'PERSON' ? row.label : `${LEVEL_ROW_LABEL[levelOf(row.scopeType)]} ${row.label}`}`}
                    />
                </div>
            </div>
            {priorLine && <span className="pr-1">{priorLine}</span>}
        </div>
    );
}

const levelOf = (s: ScopeType): TargetLevel => (s === 'PERSON' ? 'person' : s === 'TEAM' ? 'team' : s === 'COMPANY' ? 'company' : 'bu');
